import { useEffect, useRef, useState } from "react";
import useAxios from "../../utils/useAxios";
import { FaSpinner } from "react-icons/fa";
import Pagination from "../common/pagination";
import { showModal, hideModal } from "../../utils/modalUtility";
import config from "../../config";
import uploadFileToBunny from "../../utils/uploadFileToBunny";

const BASE_URL = config.apiBaseUrl || "http://localhost:5000";
const INITIAL_FORM_DATA = {
  title: "",
  property_id: "",
  state: "",
  city: "",
  location: "",
  video_file: null,
  video_url: "",
  thumbnail_media_id: null,
};

function PropertyTour() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTourId, setCurrentTourId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [properties, setProperties] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [media, setMedia] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  const videoInputRef = useRef(null);

  const axiosAPI = useAxios();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const removeFormData = () => {
    setFormData(INITIAL_FORM_DATA);
    setVideoPreview(null);
    setThumbnailPreview(null);
    setVideoUploadProgress(0);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  // ---------- Data Fetching ----------

  const getTours = async () => {
    try {
      setLoading(true);
      const response = await axiosAPI.get(`/api/property-tours?page=${page}`);
      setTours(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching property tours:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDropdownData = async () => {
    try {
      const [propertiesRes, statesRes, citiesRes, locationsRes] =
        await Promise.all([
          axiosAPI.get("/api/properties/get-properties-for-tour"),
          axiosAPI.get("/api/properties/get-states"),
          axiosAPI.get("/api/properties/get-cities"),
          axiosAPI.get("/api/properties/get-locations"),
        ]);
      setProperties(propertiesRes?.data?.properties || []);
      setStates(statesRes?.data?.data || []);
      setCities(citiesRes?.data?.data || []);
      setLocations(locationsRes?.data?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const getMedia = async () => {
    try {
      const response = await axiosAPI.get("/api/media");
      setMedia(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  useEffect(() => {
    getDropdownData();
    getMedia();
  }, []);

  useEffect(() => {
    getTours();
  }, [page]);

  // ---------- Handlers ----------

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // If a direct link was entered before, file upload takes precedence.
    setFormData((prev) => ({ ...prev, video_file: file, video_url: "" }));
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleVideoUrlChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, video_url: value, video_file: null }));
    if (videoInputRef.current) videoInputRef.current.value = "";
    setVideoUploadProgress(0);
    setVideoPreview(value || null);
  };

  const handleThumbnailSelect = (mediaItem) => {
    const alreadySelected = formData.thumbnail_media_id === mediaItem.id;
    if (alreadySelected) {
      setFormData((prev) => ({ ...prev, thumbnail_media_id: null }));
      setThumbnailPreview(null);
    } else {
      setFormData((prev) => ({ ...prev, thumbnail_media_id: mediaItem.id }));
      setThumbnailPreview(
        mediaItem.thumbnail_url ||
          mediaItem.media_url ||
          (mediaItem?.file_path && mediaItem.file_path.startsWith("http")
            ? mediaItem.file_path
            : `${BASE_URL}/${mediaItem.file_path}`),
      );
    }
  };

  // ---------- Modal Helpers ----------

  const openAddModal = () => {
    removeFormData();
    showModal("tourModal");
  };

  const openEditModal = (id) => {
    const tour = tours.find((t) => t.id === id);
    if (!tour) return;

    setIsEditMode(true);
    setCurrentTourId(id);
    setFormData({
      title: tour.title || "",
      property_id: tour.property_id || "",
      state: tour.state_id || "",
      city: tour.city_id || "",
      location: tour.location_id || "",
      video_file: null,
      video_url: tour.video_url || "",
      thumbnail_media_id: tour.thumbnail_media_id || null,
    });
    setVideoPreview(tour.video_url || null);
    setThumbnailPreview(tour.thumbnail_url || null);

    showModal("tourModal");
  };

  const closeModal = () => {
    setIsEditMode(false);
    setCurrentTourId(null);
    removeFormData();
  };

  // ---------- Submit ----------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("property_id", formData.property_id);
      payload.append("state_id", formData.state);
      payload.append("city_id", formData.city);
      payload.append("location_id", formData.location);

      if (formData.thumbnail_media_id) {
        payload.append("thumbnail_media_id", formData.thumbnail_media_id);
      }

      const trimmedVideoUrl = formData.video_url?.trim();

      if (trimmedVideoUrl) {
        payload.append("video_url", trimmedVideoUrl);
      }

      // Upload selected video to Bunny first, then submit video_id to backend.
      if (formData.video_file && !trimmedVideoUrl) {
        const signatureResponse = await axiosAPI.get(
          "/api/property-tours/get-video-id",
        );
        const videoId = signatureResponse?.data?.video_id;
        const presignedSignature = signatureResponse?.data?.presigned_signature;

        if (!videoId || !presignedSignature) {
          throw new Error("Could not generate Bunny upload credentials");
        }

        console.log(
          "Video Id and presigned signature received, starting upload...",
          videoId,
          presignedSignature,
        );

        await uploadFileToBunny({
          videoId,
          presignedSignature,
          file: formData.video_file,
          onProgress: ({ percentage }) => {
            setVideoUploadProgress(percentage);
          },
        });

        payload.append("video_id", videoId);
      }

      if (isEditMode) {
        await axiosAPI.put(
          `/api/property-tours/update/${currentTourId}`,
          payload,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        window.notifyMessage("Success", "Tour updated successfully", "success");
      } else {
        await axiosAPI.post("/api/property-tours/add", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        window.notifyMessage("Success", "Tour added successfully", "success");
      }

      hideModal("tourModal");
      closeModal();
      getTours();
    } catch (error) {
      if (error.response?.data?.message) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Delete ----------

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this tour?",
    );
    if (!isConfirmed) return;

    try {
      await axiosAPI.delete(`/api/property-tours/delete/${id}`);
      window.notifyMessage("Success", "Deleted successfully", "success");
      getTours();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  // ---------- Render ----------

  return (
    <div className="container">
      <div className="page-inner">
        {/* Header */}
        <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
          <div>
            <button
              type="button"
              className="btn btn-dark btn-round mb-4"
              onClick={openAddModal}
            >
              Add Property Tour
            </button>
          </div>
        </div>

        {/* ===== Add / Edit Modal ===== */}
        <div
          className="modal fade"
          id="tourModal"
          tabIndex="-1"
          aria-labelledby="tourModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="tourModalLabel">
                  {isEditMode ? "Edit Property Tour" : "Add Property Tour"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Title */}
                  <div className="row">
                    <div className="mb-3 col-md-12 col-sm-12">
                      <label htmlFor="title" className="form-label">
                        Tour Title
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="title"
                        placeholder="Enter tour title"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Property */}
                  <div className="row">
                    <div className="mb-3 col-md-12 col-sm-12">
                      <label htmlFor="property_id" className="form-label">
                        Select Property
                      </label>
                      <select
                        id="property_id"
                        className="form-select"
                        value={formData.property_id}
                        onChange={handleChange}
                      >
                        <option value="">-- Select Property --</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.property_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* State / City / Location */}
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="state" className="form-label">
                        Select State
                      </label>
                      <select
                        id="state"
                        className="form-select"
                        value={formData.state}
                        onChange={handleChange}
                      >
                        <option value="">-- Select State --</option>
                        {states.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="city" className="form-label">
                        Select City
                      </label>
                      <select
                        id="city"
                        className="form-select"
                        value={formData.city}
                        onChange={handleChange}
                      >
                        <option value="">-- Select City --</option>
                        {cities.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label htmlFor="location" className="form-label">
                        Select Location
                      </label>
                      <select
                        id="location"
                        className="form-select"
                        value={formData.location}
                        onChange={handleChange}
                      >
                        <option value="">-- Select Location --</option>
                        {locations.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <hr />

                  {/* Video Upload */}
                  <div className="row">
                    <div className="mb-3 col-md-12 col-sm-12">
                      <label htmlFor="video_url" className="form-label">
                        Video Link (Optional)
                      </label>
                      <input
                        type="url"
                        className="form-control mb-2"
                        id="video_url"
                        placeholder="https://example.com/video.mp4"
                        value={formData.video_url}
                        onChange={handleVideoUrlChange}
                      />
                      <small className="text-muted d-block mb-3">
                        If you provide a video link, it will be used directly.
                        If you upload a video file, it will be uploaded to Bunny
                        CDN and the generated video URL will be used.
                      </small>

                      <label htmlFor="video_upload" className="form-label">
                        Upload Video (From Local System)
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="video_upload"
                        accept="video/*"
                        ref={videoInputRef}
                        onChange={handleVideoChange}
                        disabled={Boolean(formData.video_url?.trim())}
                      />
                      <small className="text-muted">
                        Accepted formats: MP4, MOV, AVI, etc.
                      </small>
                      {isLoading && formData.video_file && (
                        <small className="d-block text-primary mt-1">
                          Uploading to Bunny: {videoUploadProgress}%
                        </small>
                      )}
                    </div>

                    {/* Video Preview */}
                    {videoPreview && (
                      <div className="mb-3 col-md-12">
                        <label className="form-label">Video Preview</label>
                        <div>
                          <video
                            src={videoPreview}
                            controls
                            style={{
                              width: "100%",
                              maxHeight: "250px",
                              borderRadius: "8px",
                              objectFit: "contain",
                              background: "#000",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Thumbnail Selection */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label mb-0">
                        Select Video Thumbnail
                        {formData.thumbnail_media_id && (
                          <span className="badge bg-success ms-2">
                            1 selected
                          </span>
                        )}
                      </label>
                      {formData.thumbnail_media_id && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              thumbnail_media_id: null,
                            }));
                            setThumbnailPreview(null);
                          }}
                        >
                          Clear Thumbnail
                        </button>
                      )}
                    </div>

                    {/* Thumbnail Preview */}
                    {thumbnailPreview && (
                      <div className="mb-3">
                        <img
                          src={thumbnailPreview}
                          alt="Selected thumbnail"
                          style={{
                            width: "160px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "2px solid #0d6efd",
                          }}
                        />
                      </div>
                    )}

                    {/* Inline Media Grid for Thumbnail */}
                    <div
                      className="border rounded p-3"
                      style={{ maxHeight: "340px", overflowY: "auto" }}
                    >
                      {media.length === 0 ? (
                        <p className="text-muted text-center mb-0">
                          No media available in the library.
                        </p>
                      ) : (
                        <div className="row g-3">
                          {media.map((item) => {
                            const isSelected =
                              formData.thumbnail_media_id === item.id;
                            return (
                              <div
                                className="col-6 col-md-3 col-lg-2"
                                key={item.id}
                              >
                                <label
                                  className={`card h-100 position-relative w-100 mb-0 ${
                                    isSelected
                                      ? "border border-primary border-2"
                                      : ""
                                  }`}
                                  style={{ cursor: "pointer" }}
                                >
                                  {/* Hidden radio input for single-select behaviour */}
                                  <input
                                    type="radio"
                                    name="thumbnail_media"
                                    value={item.id}
                                    checked={isSelected}
                                    onChange={() => handleThumbnailSelect(item)}
                                    style={{ display: "none" }}
                                  />

                                  {item.media_type === "video" ? (
                                    <video
                                      src={
                                        item.media_url ||
                                        (item?.file_path &&
                                        item.file_path.startsWith("http")
                                          ? item.file_path
                                          : `${BASE_URL}/${item.file_path}`)
                                      }
                                      muted
                                      className="card-img-top"
                                      style={{
                                        height: "90px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={
                                        item.thumbnail_url ||
                                        item.media_url ||
                                        (item?.file_path &&
                                        item.file_path.startsWith("http")
                                          ? item.file_path
                                          : `${BASE_URL}/${item.file_path}`)
                                      }
                                      alt={item.media_name}
                                      className="card-img-top"
                                      style={{
                                        height: "90px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}

                                  {/* Selected indicator */}
                                  <div
                                    className={`position-absolute top-0 end-0 m-1 rounded-circle d-flex align-items-center justify-content-center ${
                                      isSelected
                                        ? "bg-primary text-white"
                                        : "bg-white border"
                                    }`}
                                    style={{ width: "22px", height: "22px" }}
                                  >
                                    {isSelected && (
                                      <i
                                        className="fas fa-check"
                                        style={{ fontSize: "11px" }}
                                      ></i>
                                    )}
                                  </div>

                                  <div className="card-body p-1 text-center">
                                    <small
                                      className="text-muted"
                                      style={{
                                        fontSize: "10px",
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {item.media_name}
                                    </small>
                                  </div>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modal Footer inside form */}
                  <div className="modal-footer px-0">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      data-bs-dismiss="modal"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <FaSpinner className="spinner" />
                      ) : isEditMode ? (
                        "Update Tour"
                      ) : (
                        "Add Tour"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Tours Table ===== */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-12 col-md-6 mb-2 mb-md-0">
                    <h4 className="card-title">
                      All Property Tours ({tours.length})
                    </h4>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Title</th>
                        <th>Property</th>
                        <th>Location</th>
                        <th>Thumbnail</th>
                        <th>Video</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : tours.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        tours.map((tour, index) => {
                          const formattedDate = new Date(
                            tour.created_at,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          return (
                            <tr key={tour.id}>
                              <td>{(page - 1) * 10 + index + 1}</td>
                              <td>{tour.title || "—"}</td>
                              <td>{tour.property_name || "—"}</td>
                              <td>
                                {tour.location_name || tour.location || "—"}
                              </td>
                              <td>
                                {tour.thumbnail_url ? (
                                  <img
                                    src={tour.thumbnail_url}
                                    alt="Thumbnail"
                                    style={{
                                      width: "80px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                  />
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {tour.video_url ? (
                                  <a
                                    href={tour.video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-dark"
                                  >
                                    <i className="fas fa-play me-1"></i> View
                                  </a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>{formattedDate}</td>
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(tour.id)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm"
                                  onClick={() => handleDelete(tour.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Pagination totalPages={totalPages} setPage={setPage} page={page} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PropertyTour;
